import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import PageHero from "@/components/site/PageHero";
import Reveal from "@/components/site/Reveal";
import Counter from "@/components/site/Counter";
import SectionHeading from "@/components/site/SectionHeading";
import { ContactCTA } from "@/pages/Home";
import { stagger, fadeUp, viewportOnce } from "@/lib/motion";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Target, Eye, Gem } from "lucide-react";

const VALUES = [
  { icon: Gem, title: "Craft over compromise", desc: "We sweat the details others skip. Quality is non-negotiable." },
  { icon: Target, title: "Outcomes, not output", desc: "We measure success by your growth, not hours logged." },
  { icon: Eye, title: "Radical transparency", desc: "Clear communication, honest timelines, no surprises." },
];

const STATS = [
  { value: 250, suffix: "+", label: "Projects shipped" },
  { value: 40, suffix: "+", label: "Team members" },
  { value: 12, suffix: "", label: "Countries" },
  { value: 9, suffix: " yrs", label: "In business" },
];

export default function About() {
  const [team, setTeam] = useState([]);
  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
  }, []);

  return (
    <>
      <SEO title="About Us" description="Meet Web Vantage Enterprises — a premium digital agency of engineers, designers and strategists obsessed with craft and client outcomes." path="/about" />
      <PageHero
        overline="About us"
        title="We turn ambitious ideas into market-defining products"
        subtitle="Founded on the belief that digital excellence is a competitive weapon, we've spent nearly a decade helping brands win with design, engineering and growth."
        crumbs={[{ label: "About" }]}
      />

      <section className="py-20 lg:py-28">
        <div className="container-wv grid gap-12 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <div className="overflow-hidden rounded-3xl border border-white/10">
              <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Web Vantage team" loading="lazy" className="h-full w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <span className="overline">Our story</span>
            <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight text-white sm:text-4xl">
              A studio built for the next era of the web
            </h2>
            <div className="mt-6 space-y-4 text-base leading-relaxed text-slate-400">
              <p>Web Vantage Enterprises began with a simple frustration: too many digital products looked good but performed poorly, or performed well but felt lifeless. We set out to prove you could have both — beauty and results.</p>
              <p>Today we're a tight-knit team of engineers, designers and growth specialists partnering with ambitious brands across the globe. We treat every project as if our own name is on the storefront — because, in a way, it is.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16">
        <div className="container-wv">
          <motion.div variants={stagger(0.1)} initial="hidden" whileInView="show" viewport={viewportOnce} className="grid grid-cols-2 gap-8 lg:grid-cols-4">
            {STATS.map((s) => (
              <motion.div key={s.label} variants={fadeUp} className="border-l border-white/10 pl-6">
                <p className="font-heading text-4xl font-bold text-white sm:text-5xl"><Counter to={s.value} suffix={s.suffix} /></p>
                <p className="mt-2 text-sm text-slate-400">{s.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="container-wv">
          <SectionHeading overline="What we value" title="Principles that guide every decision" />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08} className="rounded-3xl border border-white/10 bg-white/[0.03] p-8">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-vantage/10 text-vantage"><v.icon className="h-6 w-6" /></div>
                <h3 className="mt-6 font-heading text-xl font-semibold text-white">{v.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{v.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="container-wv">
          <SectionHeading overline="Leadership" title="The people behind the work" align="center" className="mb-14" />
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {team.map((m, i) => (
              <Reveal key={m.id} delay={i * 0.06} className="group text-center">
                <div className="mx-auto overflow-hidden rounded-3xl border border-white/10">
                  <img src={m.avatar} alt={m.name} loading="lazy" className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <h3 className="mt-5 font-heading text-lg font-semibold text-white">{m.name}</h3>
                <p className="text-sm text-vantage">{m.role}</p>
                <p className="mt-2 text-xs leading-relaxed text-slate-400">{m.bio}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <ContactCTA />
    </>
  );
}
